// App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from "react-router-dom";
import Login from './app/auth/login/login';
import Dashboard from './app/pages/dashboard/dashboard';
import PageNotFound from './app/components/not-found';
import { Authentication } from './service/auth.utils';
import { useTokenRefresh } from './service/useTokenRefresh';
import MiniDrawer from './app/pages/layout';
import UserType from './app/pages/userManagement/userType/userType'
import ProtectedRoute from './middleware'
import ForgotPassword from './app/auth/forgotPassword/forgotPassword';
import UserTypeCreation from './app/pages/userManagement/userType/addUserType/addUserType';
import UserTypeView from './app/pages/userManagement/userType/userTypeView/userTypeView.page'
import User from './app/pages/userManagement/user/user';
import MobileUsers from './app/pages/userManagement/mobileUsers/mobileUsers';
import CountryAnalysis from './app/pages/analytics/countryAnalysis/countryAnalysis';
import SubscriptionPlans from './app/pages/licenseManagement/subscriptionPlans/subscriptionPlans';
import SubcriptionsCreation from './app/pages/licenseManagement/subscriptionPlans/addSubcriptions/addSubcriptions';
import Initiative from './app/pages/solutionManagement/initiative/initiative';
import InitiativeCreation from './app/pages/solutionManagement/initiative/addInitiative/addInitiative';
import InitiativeView from './app/pages/solutionManagement/initiative/initiativeView/initiativeView';
import InitiativePlanForm from './app/pages/licenseManagement/initiativePlans/initiativePlanForm';
import SubscriptionView from './app/pages/licenseManagement/subscriptionPlans/subscriptionView/subscriptionView';
import UserView from './app/pages/userManagement/user/userView/userView';
import EditUser from './app/pages/userManagement/user/editUser/editUser';
import UserProfile from './app/pages/userProfile/userProfile';
import CountryManagement from './app/pages/countryManagement/countryManagement';
import CountryManagementCreation from './app/pages/countryManagement/countryManagementCreation/countryManagementCreation';
import CountryManagementView from './app/pages/countryManagement/countryManagementView/countryManagementView';
import CountryLocations from './app/pages/countryManagement/countryLocations/countryLocations';
import EcosystemPartners from './app/pages/ecosystemPartners/ecosystemPartners';
import { EcosystemPartnersCreation } from './app/pages/ecosystemPartners/ecosystemPartnersCreation/ecosystemPartnersCreation';
import EcosystemPartnersView from './app/pages/ecosystemPartners/ecosystemPartnersView/ecosystemPartnersView';
import FinancialSupport from './app/pages/financialSupport/financialSupport';
import CreateFinancialSupportProgram from './app/pages/financialSupport/createFinancialSupportProgram';
import CreateFinancialSupportProgramContributionRules from './app/pages/financialSupport/createFinancialSupportProgramContributionRules';
import CreateFinancialSupportProgramPlans from './app/pages/financialSupport/createFinancialSupportProgramPlans';
import CreateFinancialSupportProgramReview from './app/pages/financialSupport/createFinancialSupportProgramReview';
import FinancialSupportView from './app/pages/financialSupport/financialSupportView';
import Settings from './app/pages/settings/settings';
import Crops from './app/pages/crops/crops';
import AddCrops from './app/pages/crops/addCrops/addCrops';
import CropsView from './app/pages/crops/cropsView/cropsView.page';
import TokenRedirect from './app/auth/tokenRedirect/tokenRedirect';
import AppCatalogue from './app/pages/settings/appCatalogue/appCatalogue';
import AddAppCatalogue from './app/pages/settings/appCatalogue/addAppCatalogue/addAppCatalogue';
import AppCatalogueView from './app/pages/settings/appCatalogue/appCatalogueView/appCatalogueView.page';
import Communications from './app/pages/settings/communications/communications';
import FeatureFlags from './app/pages/settings/featureFlags/featureFlags';
import LoginPageCards from './app/pages/settings/loginPageCards/loginPageCards';
import IntegratorRedirects from './app/pages/settings/integratorRedirects/integratorRedirects';
import QuestionnaireVersionPage from './app/pages/settings/questionnaire/questionnaireVersion/questionnaireVersion.page';
import EkScoreConfiguration from './app/pages/settings/ekScoreConfiguration/ekScoreConfiguration';
import { EK_SCORE_CONFIG_PATH, ekScoreTabPath } from './app/pages/settings/ekScoreConfiguration/ekScoreConfiguration.paths';

if (process.env.NODE_ENV !== 'test') {
  import('./App.css');
}

const LegacyQuestionnaireVersionRedirect = () => {
  const { version } = useParams();
  return <Navigate to={`${ekScoreTabPath('questionnaire')}/${version}`} replace />;
};

  export const App = () => {
  useTokenRefresh();
  const tokenAuthentication = new Authentication();
  const isAuthenticated = tokenAuthentication.isAuthenticated();

  return (
    <>

    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <Routes>
        <Route path="/" element={isAuthenticated ? (<Navigate to="/subscriptionPlans" replace />) : (<Navigate to="/auth/login" replace />)} />
        <Route path="/auth/login" element={isAuthenticated ? (<Navigate to="/subscriptionPlans" replace />) : (<Login />)} />
        <Route path="/auth/token-redirect" element={<TokenRedirect />} />
        <Route path="/auth/forgotPassword" element={isAuthenticated ? (<Navigate to="/subscriptionPlans" replace />) : (<ForgotPassword />)} />

        <Route element={<ProtectedRoute/>}>
          <Route element={<MiniDrawer />}>

            <Route path="/dashboard" element={<Dashboard />} />

            <Route path="/userProfile" element={<UserProfile />} />

            <Route path="/userType" element={<UserType />} />
            <Route path="/addUserType" element={<UserTypeCreation />} />
            <Route path="/userTypeView/:id" element={<UserTypeView />} />
            <Route path="/addUserType/:id" element={<UserTypeCreation />} />

            <Route path="/user" element={<User />} />
            <Route path="/mobileUsers" element={<MobileUsers />} />
            {/* DARE Approvals is merged into User Onboarding (approval and billing on each DARE's details). */}
            <Route path="/dareApprovals" element={<Navigate to="/user" replace />} />

            <Route path="/analytics/countryAnalysis" element={<CountryAnalysis />} />
            <Route path="/userView/:id" element={<UserView />} />
            <Route path="/editUser/:id" element={<EditUser />} />

            <Route path="/subscriptionPlans" element={<SubscriptionPlans />} />
            <Route path="/addSubcriptions" element={<SubcriptionsCreation />} />
            <Route path="/subscriptionView/:id" element={<SubscriptionView />} />
            <Route path="/addSubcriptions/:id" element={<SubcriptionsCreation />} />
            <Route path="/addInitiativePlan" element={<InitiativePlanForm />} />
            <Route path="/addInitiativePlan/:id" element={<InitiativePlanForm />} />

            <Route path="/initiative" element={<Initiative />} />
            <Route path="/addInitiative" element={<InitiativeCreation />} />
            <Route path="/initiativeView/:id" element={<InitiativeView />} />
            <Route path="/addInitiative/:id" element={<InitiativeCreation />} />

            <Route path="/countryManagement" element={<CountryManagement />} />
            <Route path="/countryManagementCreation" element={<CountryManagementCreation />} />
            <Route path="/countryManagementView/:id" element={<CountryManagementView />} />
            <Route path="/countryManagementCreation/:id" element={<CountryManagementCreation />} />
            <Route path="/countryLocations/:id" element={<CountryLocations />} />

            <Route path="/ecosystemPartners" element={<EcosystemPartners />} />
            <Route path="/ecosystemPartnersCreation" element={<EcosystemPartnersCreation />} />
            <Route path="/ecosystemPartnersView/:id" element={<EcosystemPartnersView />} />
            <Route path="/ecosystemPartnersCreation/:id" element={<EcosystemPartnersCreation />} />

            <Route path="/financialSupport" element={<FinancialSupport />} />
            <Route path="/financialSupport/create-program" element={<CreateFinancialSupportProgram />} />
            <Route path="/financialSupport/create-program/contribution-rules" element={<CreateFinancialSupportProgramContributionRules />} />
            <Route path="/financialSupport/create-program/plans" element={<CreateFinancialSupportProgramPlans />} />
            <Route path="/financialSupport/create-program/review" element={<CreateFinancialSupportProgramReview />} />
            <Route path="/financialSupport/view/:id" element={<FinancialSupportView />} />

            <Route path="/crops" element={<Crops />} />
            <Route path="/addCrops" element={<AddCrops />} />
            <Route path="/cropsView/:id" element={<CropsView />} />
            <Route path="/addCrops/:id" element={<AddCrops />} />

            <Route path="/settings" element={<Settings />} />
            <Route path="/appCatalogue" element={<AppCatalogue />} />
            <Route path="/addAppCatalogue" element={<AddAppCatalogue />} />
            <Route path="/appCatalogueView/:id" element={<AppCatalogueView />} />
            <Route path="/addAppCatalogue/:id" element={<AddAppCatalogue />} />
            <Route path="/communications" element={<Communications />} />
            <Route path="/featureFlags" element={<FeatureFlags />} />
            <Route path="/loginPageCards" element={<LoginPageCards />} />
            <Route path="/integratorRedirects" element={<IntegratorRedirects />} />
            <Route path={EK_SCORE_CONFIG_PATH} element={<Navigate to={ekScoreTabPath('questionnaire')} replace />} />
            <Route path={`${EK_SCORE_CONFIG_PATH}/:tab`} element={<EkScoreConfiguration />} />
            <Route path={`${EK_SCORE_CONFIG_PATH}/questionnaire/:version`} element={<QuestionnaireVersionPage />} />
            {/* The three screens used to be separate Configuration entries; keep old links working. */}
            <Route path="/questionnaire" element={<Navigate to={ekScoreTabPath('questionnaire')} replace />} />
            <Route path="/questionnaire/:version" element={<LegacyQuestionnaireVersionRedirect />} />
            <Route path="/climate" element={<Navigate to={ekScoreTabPath('climate')} replace />} />
            <Route path="/seasons" element={<Navigate to={ekScoreTabPath('seasons')} replace />} />

          </Route>
        </Route>

        <Route path="*" element={isAuthenticated ? <PageNotFound /> : <Navigate to="/auth/login" replace />} />
      </Routes>
    </Router>
    </>
  );
}

